import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { FaStar } from "react-icons/fa";
import axios from "axios";
import { Helmet } from "react-helmet-async";
// CircularProgress intentionally omitted in this component
import { Grid3X3, Filter } from "lucide-react";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const Categories = () => {
  const { categoryName } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [subCategory, setSubCategory] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 8;

  const subCategoryOptions = {
    "Men's Clothing": [
      "All",
      "Suits",
      "Blazers",
      "Tops",
      "Bottoms",
      "Jackets & Coats",
    ],
    "Women's Clothing": [
      "All",
      "Dresses",
      "Tops",
      "Bottoms",
      "Jackets & Coats",
    ],
    "Kids' Clothing": ["All", "Tops", "Bottoms"],
    Shoes: ["All"],
    "Bags & Accessories": ["All"],
    "Clearance Sale": ["All"],
  };

  useEffect(() => {
    const fetchCategoryProducts = async () => {
      try {
        setLoading(true);
        const subQuery =
          subCategory !== "All"
            ? `&subCategory=${encodeURIComponent(subCategory)}`
            : "";
        const res = await axios.get(
          `${SERVER_URL}/api/products?category=${encodeURIComponent(
            categoryName,
          )}${subQuery}`,
        );
        setProducts(res.data);
      } catch (err) {
        setError("We're having trouble loading products. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryProducts();
  }, [categoryName, subCategory]);

  useEffect(() => {
    setCurrentPage(1);
  }, [categoryName, subCategory]);

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = products.slice(
    indexOfFirstProduct,
    indexOfLastProduct,
  );
  const totalPages = Math.ceil(products.length / productsPerPage);

  const ProductSkeleton = () => (
    <div className="bg-white rounded-3xl p-4 shadow-sm border border-gray-100 flex flex-col justify-between">
      <div className="w-full h-64 bg-gray-100 animate-pulse rounded-2xl mb-4"></div>
      <div className="space-y-3 px-1">
        <div className="w-3/4 h-6 bg-gray-100 animate-pulse rounded-lg"></div>
        <div className="w-1/2 h-4 bg-gray-100 animate-pulse rounded"></div>
        <div className="w-1/3 h-5 bg-gray-100 animate-pulse rounded mt-2"></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen px-4 py-20 mt-12 bg-white text-gray-900 font-sans">
      <Helmet>
        <title>{categoryName} | Shop by Category - Pak Fashions</title>
        <meta
          name="description"
          content={`Explore premium fashion products under the ${categoryName} category at Pak Fashions. Stylish, affordable, and high-quality pieces.`}
        />
        <meta
          name="keywords"
          content={`fashion, ${categoryName}, clothing, style, Pak Fashions, shop category`}
        />
        <meta name="author" content="Pak Fashions" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph / Facebook */}
        <meta property="og:title" content={`${categoryName} | Pak Fashions`} />
        <meta
          property="og:description"
          content={`Discover top ${categoryName} picks curated by Pak Fashions. Quality fashion for all tastes.`}
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`https://pakfashions.co.ke/category/${categoryName}`}
        />
        <meta
          property="og:image"
          content="https://pakfashions.co.ke/pak-fashion.jpg"
        />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${categoryName} | Pak Fashions`} />
        <meta
          name="twitter:description"
          content={`Explore top-rated ${categoryName} items at Pak Fashions. Clothing & fashion redefined.`}
        />
        <meta
          name="twitter:image"
          content="https://pakfashions.co.ke/pak-circle.png"
        />
      </Helmet>

      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
            <h1 className="text-3xl md:text-5xl font-black text-gray-900 tracking-tight flex items-center">
              <Grid3X3 className="w-8 h-8 md:w-10 md:h-10 text-yellow-500 mr-4" />
              {categoryName}
            </h1>
          </div>
          <div className="h-px bg-gradient-to-r from-gray-200 to-transparent w-full"></div>
        </div>

        {/* Subcategory Filter Buttons */}
        {(subCategoryOptions[categoryName] || []).length > 1 && (
          <div className="mb-12 bg-gray-50 border border-gray-100 rounded-[2rem] p-4 flex items-center shadow-[0_4px_20px_rgb(0,0,0,0.02)]">
            <div className="hidden md:flex items-center text-gray-500 font-bold mr-6 ml-4">
              <Filter className="w-5 h-5 mr-2" /> Filter
            </div>
            <div className="flex flex-wrap gap-3 flex-1">
              {subCategoryOptions[categoryName].map((sub) => (
                <button
                  key={sub}
                  onClick={() => setSubCategory(sub)}
                  className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all duration-300 ${
                    subCategory === sub
                      ? "bg-gray-900 text-white shadow-md shadow-gray-200 translate-y-px"
                      : "bg-white text-gray-600 border border-gray-200 hover:border-yellow-400 hover:text-yellow-600 shadow-sm"
                  }`}
                >
                  {sub}
                </button>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <ProductSkeleton key={n} />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-20 bg-red-50 border border-red-100 rounded-[2rem] text-red-600 font-medium max-w-2xl mx-auto shadow-sm">
            {error}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 bg-gray-50 border border-gray-100 rounded-[3rem] max-w-3xl mx-auto shadow-sm">
            <Filter className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-xl text-gray-900 font-bold mb-2">
              No products found
            </p>
            <p className="text-gray-500 font-medium">
              Try adjusting your category filters to find what you're looking
              for.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 xl:gap-8">
            {currentProducts.map((product) => (
              <Link
                to={`/product/${product._id}`}
                key={product._id}
                className="group bg-white border border-gray-100 rounded-[2rem] p-4 shadow-sm hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] hover:border-yellow-200 transition-all duration-500 hover:-translate-y-1 block"
              >
                <div className="relative h-64 sm:h-72 overflow-hidden rounded-2xl bg-gray-50 mb-5">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition duration-700 ease-in-out"
                  />
                  {!product.inStock && (
                    <div className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                      Sold Out
                    </div>
                  )}
                </div>
                <div className="px-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1 block">
                    {product.subCategory || product.category}
                  </span>
                  <h2 className="text-lg font-bold text-gray-900 mb-2 truncate group-hover:text-yellow-600 transition-colors">
                    {product.name}
                  </h2>

                  <div className="flex items-center justify-between mb-2">
                    <p className="text-gray-900 font-black text-lg">
                      Ksh {product.price.toLocaleString()}
                    </p>
                    <div className="flex items-center gap-1">
                      <FaStar className="text-yellow-400 text-sm" />
                      <span className="text-sm font-medium text-gray-500">
                        {product.rating || "5.0"}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-16 flex justify-center">
          <div className="flex items-center bg-gray-50 border border-gray-100 rounded-full p-2 shadow-sm">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-6 py-2.5 rounded-full font-bold text-sm transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white hover:shadow-sm"
            >
              Previous
            </button>

            <div className="px-4 border-l border-r border-gray-200 flex gap-2">
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all shadow-sm ${
                    currentPage === i + 1
                      ? "bg-gray-900 text-white"
                      : "bg-white text-gray-600 hover:bg-yellow-50 hover:text-yellow-600"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="px-6 py-2.5 rounded-full font-bold text-sm transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white hover:shadow-sm"
            >
              Next Outfits
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;
